import React, {
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
  useCallback,
  useRef
} from 'react'
import { Empty, message } from 'antd'
import { useDispatch, useSelector, useStore } from 'react-redux'
import type { RootState } from '../../app/store'
import CONFIG from '../../constants/config'
import Photo from '../components/Photo'
import Viewer from '../components/ViewerV2'
import HeaderExtra from './components/HeaderExtra'
import type { Photo as PhotoType } from './types'
import type { FilterState } from './components/PhotoFilter/types'
import { selectSearchFilter } from '../../slices/photoFilterSlice'
import { selectSearchTrackingId } from '../../slices/photoFilterSlice'
import {
  appendPhotos,
  clearCheck,
  pushCheckedPhoto,
  removeCheckedPhoto,
  pushUncheckedPhoto,
  removeUncheckedPhoto,
  selectCheckedPhotos,
  selectUncheckedPhotos,
  selectCheckAll,
  selectPhotosNum,
  selectCanFavorite,
  setCanFavorite,
  setCheckAll,
  setPhotos,
  setPhotosNum
} from '../../slices/photosByContentSlice'
import { setLoading as setGlobalLoading } from '../../actions/main'
import { normalizeToSet } from './utils/selectionHelpers'
import { capturePromiseException } from '../../utils/sentryUtils'
import fetchHelper from '../../utils/fetchHelper'
import { buildParamsWithAuthPolicy } from '../../actions/photo'
import type { Dayjs } from 'dayjs'
import { useNavigate } from 'react-router-dom'
import {
  isB8059TrackContextReady,
  trackWorkgroupB8059Click
} from '../components/PhotoListAggregateEntries/track'

declare const __API_SERVER_HOST__: string

const HourlyTimeline: React.FC<{ photos: PhotoType[] }> = ({ photos }) => {
  const hourlyCounts = useMemo(() => {
    const counts = new Array(24).fill(0)
    photos.forEach((photo) => {
      // photo.time 单位：兼容秒和毫秒
      const ts = photo.time > 1e12 ? photo.time : photo.time * 1000
      const hour = new Date(ts).getHours()
      if (hour >= 0 && hour < 24) counts[hour]++
    })
    return counts
  }, [photos])

  return (
    <div className="user-agg-timeline">
      <div className="user-agg-timeline-track">
        <div className="user-agg-timeline-slots">
          {hourlyCounts.map((count, hour) => (
            <div key={hour} className="user-agg-timeline-slot">
              {count > 0 && (
                <span
                  className={`user-agg-timeline-badge user-agg-timeline-badge--h${Math.min(
                    count,
                    3
                  )}`}
                >
                  {count}
                </span>
              )}
              <span className="user-agg-timeline-dash" />
            </div>
          ))}
        </div>
      </div>
      <div className="user-agg-timeline-labels">
        <span>0h</span>
        <span>6h</span>
        <span>12h</span>
        <span>18h</span>
        <span>24h</span>
      </div>
    </div>
  )
}

type AggPhotoByUserPerson = {
  userID: string
  nickname: string
  headImageURL: string
  photos: PhotoType[]
}

const formatAggTimes = (
  times: [Dayjs, Dayjs] | null | undefined
): Array<{ start: string; end: string }> => {
  if (!times || !times[0] || !times[1]) return []
  return [
    {
      start: times[0].format(CONFIG.timeFormat),
      end: times[1].format(CONFIG.timeFormat)
    }
  ]
}

const PhotosListByUserAgg: React.FC<{
  photoListRef?: React.RefObject<any>
}> = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const store = useStore()

  const queryFilter = useSelector(selectSearchFilter) as FilterState | null
  const groupId = useSelector((state: RootState) => state.group.groupId)
  const searchTrackingId = useSelector(selectSearchTrackingId)
  const uiFilter = useSelector(
    (state: RootState) => state.photoFilter?.uiFilter
  ) as FilterState | undefined

  // 选择状态复用 photov2 content 的 slice，HeaderExtra 也会使用同一套选择器
  const photosNum = useSelector(selectPhotosNum)
  const canFavorite = useSelector(selectCanFavorite)
  const checkedPhotosRaw = useSelector(selectCheckedPhotos)
  const unCheckedPhotosRaw = useSelector(selectUncheckedPhotos)
  const checkAll = useSelector(selectCheckAll)

  const checkedPhotos = useMemo(
    () => normalizeToSet(checkedPhotosRaw),
    [checkedPhotosRaw]
  )
  const unCheckedPhotos = useMemo(
    () => normalizeToSet(unCheckedPhotosRaw),
    [unCheckedPhotosRaw]
  )

  const [people, setPeople] = useState<AggPhotoByUserPerson[]>([])
  const [loading, setLocalLoading] = useState(false)
  const [error, setError] = useState<string>('')
  const [pageNo, setPageNo] = useState<number>(1)
  const [totalPage, setTotalPage] = useState<number>(1)
  const loadingMoreRef = useRef(false)
  /** 每人区块「查看更多」展开后展示全部照片 */
  const [expandedByUser, setExpandedByUser] = useState<Record<string, boolean>>(
    {}
  )
  const [viewerUserID, setViewerUserID] = useState<string>('')
  const rootRef = useRef<HTMLDivElement | null>(null)
  const scrollRef = useRef<HTMLDivElement | null>(null)
  const viewerRef = useRef<any>(null)
  const [contentWidth, setContentWidth] = useState<number>(0)

  // 记录当前请求参数，避免并发切换时写回过期数据
  const latestReqIdRef = useRef(0)
  const lastRequestSignatureRef = useRef<string>('')

  const page1RequestSignature = useMemo(() => {
    if (!queryFilter?.groupByUser || !groupId) return ''
    const departmentIDList = queryFilter.departments?.map((d) => d.id) || []
    const userIDList =
      queryFilter.departmentUsers?.map((u: any) => u.userID || u.id) || []
    const locations =
      (queryFilter.locations || []).map(
        (loc: any) => loc?.name || loc?.detail
      ) || []
    const suggestedSearchWords = (
      queryFilter.useSuggestedSearch === false
        ? []
        : (queryFilter.suggestedSearchWords || []).filter(
            (w: any) => w?.isChecked
          )
    ).map((w: any) => w.word)

    return JSON.stringify({
      groupId,
      searchKeywords: queryFilter.searchKeywords || [],
      mediaType: queryFilter.mediaType,
      sourceType: queryFilter.sourceType,
      times: formatAggTimes(queryFilter.times),
      userIDList,
      departmentIDList,
      watermarks: queryFilter.watermarks || [],
      locations,
      useSegment: queryFilter.useSegment,
      useSynonym: queryFilter.useSynonym,
      useSuggestedSearch: queryFilter.useSuggestedSearch,
      suggestedSearchWords,
      existWatermark: queryFilter.existWatermark ?? 0,
      // 用于确保每次点击“搜索”都能触发一次拉取
      searchTrackingId
    })
  }, [queryFilter, groupId, searchTrackingId])

  const buildAggReturnSnapshotKey = () =>
    `${Date.now()}_${Math.random().toString(16).slice(2)}`

  const serializeFilterForStorage = (filter: any) => {
    if (!filter) return filter
    const next = { ...filter }
    const times = next.times
    if (Array.isArray(times) && times[0] && times[1]) {
      next.times = [
        typeof times[0]?.toISOString === 'function'
          ? times[0].toISOString()
          : times[0],
        typeof times[1]?.toISOString === 'function'
          ? times[1].toISOString()
          : times[1]
      ]
    }
    return next
  }

  const handleViewAllClick = useCallback(
    (memberId: string) => {
      if (isB8059TrackContextReady()) {
        trackWorkgroupB8059Click({
          clickItem: 'viewAllMemberPhotos',
          popType: '按人员',
          string1: memberId
        })
      }
      const snapshotKey = buildAggReturnSnapshotKey()
      try {
        const snapshot = {
          type: 'agg_user_return',
          createdAt: Date.now(),
          uiFilter: serializeFilterForStorage(uiFilter),
          searchFilter: serializeFilterForStorage(queryFilter)
        }
        sessionStorage.setItem(
          `photoAggReturn:${snapshotKey}`,
          JSON.stringify(snapshot)
        )
      } catch (e) {
        // 忽略存储失败，仍可跳转
      }

      navigate(
        `/photo/all?member_id=${encodeURIComponent(
          memberId
        )}&from=agg_user&snapshotKey=${encodeURIComponent(snapshotKey)}`
      )
    },
    [dispatch, queryFilter, uiFilter]
  )

  const handlePushCheckedPhoto = useCallback(
    (photo: PhotoType) => {
      if (checkAll) {
        dispatch(removeUncheckedPhoto(photo.seq))
        return
      }

      // 当即将选满所有可选照片时，切到全选模式
      if (checkedPhotos.size === Math.max(photosNum - 1, 0)) {
        dispatch(setCheckAll())
        return
      }

      dispatch(pushCheckedPhoto(photo.seq))
    },
    [checkAll, checkedPhotos.size, photosNum, dispatch]
  )

  const handleRemoveCheckedPhoto = useCallback(
    (photo: PhotoType) => {
      if (checkAll) {
        // 取消全选：将该照片标记到 unCheckedPhotos（或清空全选）
        if (unCheckedPhotos.size === Math.max(photosNum - 1, 0)) {
          dispatch(clearCheck())
        } else {
          dispatch(pushUncheckedPhoto(photo.seq))
        }
        return
      }

      dispatch(removeCheckedPhoto(photo.seq))
    },
    [checkAll, unCheckedPhotos.size, photosNum, dispatch]
  )

  const fetchAggUserPage = useCallback(
    async (pageNo: number) => {
      if (!groupId || !queryFilter) return

      const reqId = ++latestReqIdRef.current
      dispatch(setGlobalLoading(true))
      setLocalLoading(true)
      setError('')

      const departmentIDList = queryFilter.departments?.map((d) => d.id) || []
      const userIDList =
        queryFilter.departmentUsers?.map((u: any) => u.userID || u.id) || []

      const suggestedSearchWords = (queryFilter.suggestedSearchWords || [])
        .filter((w: any) => w && w.isChecked)
        .map((w: any) => w.word)

      const body = {
        filter: {
          searchKeywords: queryFilter.searchKeywords || [],
          mediaType: queryFilter.mediaType,
          sourceType: queryFilter.sourceType,
          times: formatAggTimes(queryFilter.times),
          userIDList: userIDList as string[],
          departmentIDList: departmentIDList as string[],
          watermarks: queryFilter.watermarks || [],
          locations:
            (queryFilter.locations || []).map(
              (loc) => loc?.name || loc?.detail
            ) || [],
          useSegment: queryFilter.useSegment,
          useSynonym: queryFilter.useSynonym,
          useSuggestedSearch: queryFilter.useSuggestedSearch,
          suggestedSearchWords:
            queryFilter.useSuggestedSearch === false
              ? []
              : suggestedSearchWords || [],
          excludeKeywords: [],
          existWatermark: queryFilter.existWatermark ?? 0
        },
        groupID: groupId,
        pageNo
      }

      try {
        const result = await fetchHelper(dispatch, store.getState, {
          url: __API_SERVER_HOST__ + '/v5/workgroup/photos/agg/user',
          arguments: {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify(
              buildParamsWithAuthPolicy(store.getState(), body)
            )
          }
        })

        if (latestReqIdRef.current !== reqId) return

        const { payload, status } = result || {}
        if (status !== 0 || !payload) {
          const msg = '按人员聚合请求失败'
          setError(msg)
          message.error(msg)
          return
        }

        const nextPeople: AggPhotoByUserPerson[] = (payload.people || []) as any
        const nextFlatPhotos: PhotoType[] = nextPeople.flatMap(
          (p) => (p.photos || []) as PhotoType[]
        )

        const nextPhotosNum = payload.photoTotal
        const nextTotalPage =
          typeof payload.totalPage === 'number' && payload.totalPage > 0
            ? payload.totalPage
            : 1

        if (pageNo === 1) {
          dispatch(clearCheck())
          dispatch(setPhotos(nextFlatPhotos))
          dispatch(setPhotosNum(nextPhotosNum))
        } else {
          dispatch(appendPhotos(nextFlatPhotos))
          dispatch(setPhotosNum(nextPhotosNum))
        }

        dispatch(setCanFavorite(Boolean(payload.canSetFavorite)))
        setTotalPage(nextTotalPage)
        setPageNo(pageNo)

        if (pageNo === 1) {
          setPeople(nextPeople)
        } else {
          setPeople((prev) => [...prev, ...nextPeople])
        }
      } catch (e) {
        capturePromiseException(e)
        if (latestReqIdRef.current !== reqId) return
        const msg = (e as any)?.message || '按人员聚合请求失败'
        setError(msg)
        message.error(msg)
      } finally {
        if (latestReqIdRef.current === reqId) {
          dispatch(setGlobalLoading(false))
          setLocalLoading(false)
        }
      }
    },
    [groupId, queryFilter, dispatch, store]
  )

  // 首屏拉取：当切换到按人员模式时请求 agg/user
  useEffect(() => {
    if (!page1RequestSignature) return
    if (page1RequestSignature === lastRequestSignatureRef.current) return
    lastRequestSignatureRef.current = page1RequestSignature

    // 每次筛选变更都从第一页开始（分页追加逻辑后续接滚动）
    setPageNo(1)
    setTotalPage(1)
    loadingMoreRef.current = false
    fetchAggUserPage(1)
  }, [page1RequestSignature, fetchAggUserPage])

  useEffect(() => {
    setExpandedByUser({})
  }, [page1RequestSignature])

  // 未展开状态：展示“恰好一行”，最后一格用于「查看更多」
  useLayoutEffect(() => {
    const el = rootRef.current
    if (!el) return

    const compute = () => {
      const style = window.getComputedStyle(el)
      const pl = Number.parseFloat(style.paddingLeft || '0') || 0
      const pr = Number.parseFloat(style.paddingRight || '0') || 0
      const w = Math.max(0, el.clientWidth - pl - pr)
      setContentWidth(w)
    }

    compute()

    if (typeof ResizeObserver === 'undefined') return
    const ro = new ResizeObserver(() => compute())
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  const colsPerRow = useMemo(() => {
    const COLUMN_WIDTH = 238 // 对齐 PhotosList 的 columnWidth
    // 每个用户卡片左右 padding 16px * 2，再减一点安全值避免临界像素导致换行
    const CARD_HORIZONTAL_PADDING = 32
    const SAFETY_PX = 2
    const available = Math.max(
      0,
      (contentWidth || 0) - CARD_HORIZONTAL_PADDING - SAFETY_PX
    )
    return Math.max(1, Math.floor(available / COLUMN_WIDTH))
  }, [contentWidth])

  const viewerPhotos = useMemo(() => {
    const targetPerson = people.find((person) => person.userID === viewerUserID)
    return targetPerson?.photos || []
  }, [people, viewerUserID])

  const openViewerByUserPhoto = useCallback(
    (personUserID: string, photoSeq: any) => {
      const targetPerson = people.find(
        (person) => person.userID === personUserID
      )
      if (!targetPerson) return

      const index = (targetPerson.photos || []).findIndex(
        (p) => p.seq === photoSeq
      )
      if (index < 0) return

      setViewerUserID(personUserID)
      requestAnimationFrame(() => {
        if (viewerRef.current) {
          viewerRef.current.view(index)
        }
      })
    },
    [people]
  )

  const readyToCheck =
    checkedPhotos.size > 0 || unCheckedPhotos.size > 0 || checkAll

  const hasMore = pageNo < totalPage

  const handleScroll = useCallback(() => {
    const el = scrollRef.current
    if (!el) return
    if (!hasMore) return
    if (loadingMoreRef.current) return
    if (loading) return

    const threshold = 240
    const distanceToBottom = el.scrollHeight - el.scrollTop - el.clientHeight
    if (distanceToBottom > threshold) return

    loadingMoreRef.current = true
    fetchAggUserPage(pageNo + 1).finally(() => {
      loadingMoreRef.current = false
    })
  }, [fetchAggUserPage, hasMore, loading, pageNo])

  if (loading && people.length === 0) {
    return (
      <div ref={rootRef} className="photoList photo-list-by-user-agg">
        <HeaderExtra dataSource="content" />
        <div
          ref={scrollRef}
          className="photo-list-by-user-agg__scroll"
          onScroll={handleScroll}
        >
          <Empty description="按人员列表加载中..." />
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div ref={rootRef} className="photoList photo-list-by-user-agg">
        <HeaderExtra dataSource="content" />
        <div
          ref={scrollRef}
          className="photo-list-by-user-agg__scroll"
          onScroll={handleScroll}
        >
          <Empty description={error} />
        </div>
      </div>
    )
  }

  return (
    <div ref={rootRef} className="photoList photo-list-by-user-agg">
      <HeaderExtra dataSource="content" />

      <div
        ref={scrollRef}
        className="photo-list-by-user-agg__scroll"
        onScroll={handleScroll}
      >
        {people.length === 0 ? (
          <Empty description="暂无按人员聚合结果" />
        ) : (
          <div>
            {people.map((person) => {
              const allPhotos = person.photos || []
              const total = allPhotos.length
              const isExpanded = Boolean(expandedByUser[person.userID])
              const collapsedSlots = colsPerRow
              const collapsedPhotoCount =
                total > collapsedSlots ? Math.max(collapsedSlots - 1, 0) : total
              const personPhotos = isExpanded
                ? allPhotos
                : allPhotos.slice(0, collapsedPhotoCount)
              const showViewMore = !isExpanded && total > collapsedPhotoCount
              const remaining = Math.max(total - collapsedPhotoCount, 0)

              return (
                <div key={person.userID} className="user-agg-block">
                  <div className="user-agg-header">
                    <img
                      className="user-agg-avatar"
                      src={person.headImageURL}
                      alt={person.nickname}
                    />
                    <div className="user-agg-title-wrap">
                      <span className="user-agg-name">{person.nickname}</span>
                      <span className="user-agg-count">共 {total} 张</span>
                    </div>
                    <HourlyTimeline photos={allPhotos} />
                    <a
                      className="user-agg-view-all"
                      href="#"
                      onClick={(e) => {
                        e.preventDefault()
                        handleViewAllClick(person.userID)
                      }}
                    >
                      查看全部 &gt;
                    </a>
                  </div>

                  <div className="user-agg-photo-wrap">
                    <div className="user-agg-photo-row">
                      {personPhotos.map((photo) => {
                        const isChecked = checkAll
                          ? !unCheckedPhotos.has(photo.seq)
                          : checkedPhotos.has(photo.seq)

                        return (
                          <div key={photo.seq} className="user-agg-photo-cell">
                            <Photo
                              style={{ margin: 0 }}
                              photo={photo}
                              canCheck
                              readyToCheck={readyToCheck}
                              checked={isChecked}
                              onPreview={() =>
                                openViewerByUserPhoto(person.userID, photo.seq)
                              }
                              onCheck={() => handlePushCheckedPhoto(photo)}
                              onUnCheck={() => handleRemoveCheckedPhoto(photo)}
                            />
                          </div>
                        )
                      })}
                      {showViewMore ? (
                        <div className="user-agg-photo-cell">
                          <button
                            type="button"
                            className="user-agg-view-more"
                            onClick={() =>
                              setExpandedByUser((prev) => ({
                                ...prev,
                                [person.userID]: true
                              }))
                            }
                          >
                            <span
                              className="user-agg-view-more-icon"
                              aria-hidden
                            >
                              ›
                            </span>
                            <span className="user-agg-view-more-title">
                              查看更多
                            </span>
                            <span className="user-agg-view-more-sub">
                              还有 {remaining} 张
                            </span>
                          </button>
                        </div>
                      ) : null}
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        )}
        {people.length > 0 ? (
          <div
            style={{ textAlign: 'center', padding: '10px 0', color: '#8c8c8c' }}
          >
            {loading && hasMore
              ? '加载中...'
              : hasMore
                ? '下拉加载更多'
                : '没有更多了'}
          </div>
        ) : null}
      </div>
      <Viewer
        ref={viewerRef}
        // @ts-expect-error ViewerV2 的 photos 类型与本页 Photo[] 不完全一致
        photos={viewerPhotos}
        canFavorite={canFavorite}
        showWatermarkAction={true}
        isShowComments={true}
        fromPlace="allPhoto"
      />
    </div>
  )
}

export default PhotosListByUserAgg
